/**
 * WVB-04 — Financial Context Widget
 * Displays the event's financial truth: contract revenue vs. approved budget,
 * budgeted gross margin, financial health, and pending revision status.
 *
 * Used in EventFinancialsWorkspace as a new "Context" tab.
 */
import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  CircleDashed, RefreshCw, Loader2, AlertCircle, Clock,
  DollarSign, ShieldCheck, Info, ExternalLink,
} from "lucide-react";
import { financialApi } from "@/services/api";
import { toast } from "sonner";

// ── Health badge ──────────────────────────────────────────────────────────────

const HEALTH_CONFIG = {
  HEALTHY:                    { icon: CheckCircle,  color: "text-[#2E844A]",  bg: "bg-[#EAF5EE]",  label: "Healthy" },
  MARGIN_WARNING:             { icon: AlertTriangle,color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]",  label: "Margin Warning" },
  MARGIN_CRITICAL:            { icon: AlertTriangle,color: "text-[#BA0517]",  bg: "bg-[#FCEBEC]",  label: "Margin Critical" },
  BUDGET_EXCEEDS_CONTRACT:    { icon: TrendingDown, color: "text-[#BA0517]",  bg: "bg-[#FCEBEC]",  label: "Exceeds Contract" },
  NO_APPROVED_BUDGET:         { icon: CircleDashed, color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "No Approved Budget" },
  MARGIN_POLICY_NOT_CONFIGURED: { icon: Info,       color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "Policy Not Configured" },
  BOOKING_NOT_FOUND:          { icon: CircleDashed, color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "No Booking" },
  BOOKING_CANCELLED:          { icon: AlertCircle,  color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "Booking Cancelled" },
  CURRENCY_MISMATCH:          { icon: AlertTriangle,color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]",  label: "Currency Mismatch" },
  REVENUE_UNAVAILABLE:        { icon: AlertCircle,  color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "Revenue Unavailable" },
};

function HealthBadge({ code, large = false }) {
  const cfg = HEALTH_CONFIG[code] || { icon: Info, color: "text-[#706E6B]", bg: "bg-[#F3F3F3]", label: code };
  const Icon = cfg.icon;
  return (
    <span data-testid={`financial-health-${code}`}
      className={`inline-flex items-center gap-1.5 ${large ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs"} rounded-full font-semibold ${cfg.bg} ${cfg.color}`}>
      <Icon size={large ? 14 : 11} />
      {cfg.label}
    </span>
  );
}

// ── KPI metric tile ───────────────────────────────────────────────────────────

function MetricTile({ label, value, sub, accent = "border-[#E5E5E5]", testId }) {
  return (
    <div data-testid={testId} className={`bg-white border rounded-xl p-4 ${accent}`}>
      <p className="text-xs text-[#706E6B] font-medium">{label}</p>
      <p className="text-lg font-bold text-[#1C1917] mt-0.5">{value || "—"}</p>
      {sub && <p className="text-xs text-[#706E6B] mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────

export default function FinancialContextWidget({ eid }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financialApi.getContext(eid);
      setCtx(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You do not have permission to view financial context for this event.");
      } else if (e.response?.status === 404) {
        setError("Financial context not available for this event.");
      } else {
        setError(e.response?.data?.detail || "Failed to load financial context");
      }
    } finally {
      setLoading(false);
    }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-[#0176D3]" />
        <span className="ml-2 text-sm text-[#706E6B]">Loading financial context…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-8 text-[#706E6B]" data-testid="financial-context-error">
        <AlertCircle size={16} />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (!ctx) return null;

  const fm = ctx.financial_metrics || {};
  const apr = ctx.current_approved_budget || {};
  const rev = ctx.contract_revenue || {};
  const pol = ctx.margin_policy || {};
  const pending = ctx.pending_revision || {};
  const approval = ctx.approval_state || {};

  const marginPct = fm.gross_margin_percent;
  const marginNum = marginPct != null ? parseFloat(marginPct) : null;
  const marginColor = marginNum == null ? "text-[#706E6B]"
    : marginNum < 0 ? "text-[#BA0517] font-bold"
    : marginNum < 10 ? "text-[#8C4B00] font-semibold"
    : "text-[#2E844A] font-semibold";

  return (
    <div className="space-y-5" data-testid="financial-context-widget">

      {/* Health header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <HealthBadge code={ctx.financial_health} large />
            {pending.available && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#8C4B00] bg-[#FFF4E5] px-2 py-0.5 rounded-full">
                <Clock size={11} />
                Revision v{pending.budget_version} pending
              </span>
            )}
          </div>
          <p className="text-xs text-[#706E6B] mt-1.5 max-w-lg">{ctx.financial_health_reason}</p>
        </div>
        <button
          data-testid="financial-context-refresh"
          onClick={load}
          className="text-[#706E6B] hover:text-[#0176D3] p-1.5 rounded-lg hover:bg-[#0176D3]/10 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Core metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="financial-context-metrics">
        <MetricTile
          label="Contract Revenue"
          value={rev.value_display}
          sub={rev.currency_code + (rev.availability !== "AVAILABLE" ? ` · ${rev.availability}` : "")}
          testId="financial-metric-revenue"
          accent={rev.availability === "AVAILABLE" ? "border-[#E5E5E5]" : "border-[#D8D8D8] bg-[#F8F6F1]"}
        />
        <MetricTile
          label="Approved Budget"
          value={apr.total_display}
          sub={apr.available ? `v${apr.budget_version} · ${apr.budget_number}` : "No approved budget"}
          testId="financial-metric-budget"
          accent={apr.available ? "border-[#E5E5E5]" : "border-[#D8D8D8] bg-[#F8F6F1]"}
        />
        <MetricTile
          label="Budgeted Gross Profit"
          value={fm.gross_profit_display}
          sub="Revenue minus approved cost"
          testId="financial-metric-gross-profit"
          accent={
            fm.gross_profit_minor == null ? "border-[#D8D8D8] bg-[#F8F6F1]"
            : fm.gross_profit_minor < 0 ? "border-[#BA0517]/20 bg-[#FCEBEC]/30"
            : "border-[#2E844A]/20 bg-[#EAF5EE]/30"
          }
        />
      </div>

      {/* Margin + policy row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="financial-metric-margin">
          <p className="text-xs text-[#706E6B] font-medium">Budgeted Gross Margin</p>
          {marginPct != null ? (
            <p className={`text-2xl mt-0.5 ${marginColor}`}>{marginPct}%</p>
          ) : (
            <p className="text-lg font-bold text-[#706E6B] mt-0.5">—</p>
          )}
          <p className="text-[10px] text-[#706E6B] mt-1">Budgeted · not forecast</p>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="financial-metric-policy">
          <p className="text-xs text-[#706E6B] font-medium">Margin Policy</p>
          {pol.available ? (
            <>
              <p className="text-sm font-semibold text-[#1C1917] mt-0.5">{pol.policy_name}</p>
              <p className="text-xs text-[#706E6B] mt-0.5">
                Min: {pol.min_margin_percent}% · Critical: {pol.critical_margin_percent}%
              </p>
            </>
          ) : (
            <p className="text-sm text-[#706E6B] italic mt-0.5">Not configured</p>
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="financial-metric-approval">
          <p className="text-xs text-[#706E6B] font-medium">Approval State</p>
          <p className="text-sm font-semibold text-[#1C1917] mt-0.5">
            {approval.status || "No approval"}
          </p>
          {approval.approval_number && (
            <p className="text-xs text-[#706E6B] mt-0.5">{approval.approval_number}</p>
          )}
        </div>
      </div>

      {/* Pending revision notice */}
      {pending.available && (
        <div className="flex items-start gap-2 bg-[#FFF4E5] border border-[#FFD59E] rounded-xl p-4" data-testid="financial-context-pending-revision">
          <Clock size={16} className="text-[#8C4B00] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-[#8C4B00]">
              Revised Budget Pending: v{pending.budget_version} ({pending.budget_number}) — {pending.status}
            </p>
            <p className="text-xs text-[#8C4B00] mt-0.5">
              {pending.total_display} · The approved baseline above (v{apr.budget_version}) remains current until this revision is approved.
            </p>
          </div>
        </div>
      )}

      {/* Data notes */}
      <div className="bg-[#F8F6F1] border border-[#E5E5E5] rounded-xl p-3" data-testid="financial-context-data-notes">
        <p className="text-xs text-[#706E6B] font-semibold mb-1">Data Scope</p>
        <ul className="space-y-0.5">
          <li className="text-xs text-[#706E6B]">
            <span className="font-medium">Actual costs:</span> {ctx.data_notes?.actual_costs}
          </li>
          <li className="text-xs text-[#706E6B]">
            <span className="font-medium">Open commitments:</span> {ctx.data_notes?.open_commitments}
          </li>
        </ul>
        {ctx.data_freshness && (
          <p className="text-[10px] text-[#706E6B] mt-1.5">
            Data refreshed: {new Date(ctx.data_freshness).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
